import React, { useCallback, useEffect, useState } from "react";

import {
	Button,
	Chip,
	IconButton,
	LinearProgress,
	makeStyles,
	Paper,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@material-ui/core";
import {
	DeleteOutline,
	PauseCircleOutline,
	PlayCircleOutline,
	StopOutlined,
	VisibilityOutlined,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import ConfirmationModal from "../../components/ConfirmationModal";
import ResponsiveTable from "../../components/ResponsiveTable";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { formatDuration } from "../Dashboard/format";
import CampaignModal from "./CampaignModal";
import CampaignDetails from "./CampaignDetails";
import { CAMPAIGN_STATUS_COLOR, percentDone } from "./status";

const REFRESH_MS = 5000;

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "scroll",
		...theme.scrollbarStyles,
	},
	progress: {
		minWidth: 120,
	},
	empty: {
		padding: theme.spacing(4),
		textAlign: "center",
		color: theme.palette.text.secondary,
	},
	hint: {
		margin: theme.spacing(0, 0, 1.5),
		color: theme.palette.text.secondary,
	},
}));

const Campaigns = () => {
	const classes = useStyles();

	const [campaigns, setCampaigns] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [detailsId, setDetailsId] = useState(null);
	const [confirm, setConfirm] = useState(null); // { action, campaign }

	const load = useCallback(async () => {
		try {
			const { data } = await api.get("/campaigns");
			setCampaigns(data.campaigns);
		} catch (err) {
			toastError(err);
		} finally {
			setLoaded(true);
		}
	}, []);

	useEffect(() => {
		load();
		const timer = setInterval(load, REFRESH_MS);
		return () => clearInterval(timer);
	}, [load]);

	const act = async (campaign, action) => {
		try {
			if (action === "delete") {
				await api.delete(`/campaigns/${campaign.id}`);
			} else {
				await api.post(`/campaigns/${campaign.id}/${action}`);
			}
			await load();
		} catch (err) {
			toastError(err);
		}
	};

	const handleConfirm = async () => {
		const { action, campaign } = confirm;
		setConfirm(null);
		await act(campaign, action);
		if (action === "delete") toast.success(i18n.t("campaigns.toasts.deleted"));
	};

	const canDelete = status =>
		status === "draft" || status === "completed" || status === "canceled";

	return (
		<MainContainer>
			<ConfirmationModal
				title={
					confirm &&
					`${i18n.t(`campaigns.confirm.${confirm.action}.title`)} "${
						confirm.campaign.name
					}"?`
				}
				open={!!confirm}
				onClose={() => setConfirm(null)}
				onConfirm={handleConfirm}
			>
				{confirm && i18n.t(`campaigns.confirm.${confirm.action}.message`)}
			</ConfirmationModal>

			<CampaignModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				onChanged={load}
			/>
			<CampaignDetails
				campaignId={detailsId}
				onClose={() => setDetailsId(null)}
				onChanged={load}
			/>

			<MainHeader>
				<Title>{i18n.t("campaigns.title")}</Title>
				<MainHeaderButtonsWrapper>
					<Button
						variant="contained"
						color="primary"
						onClick={() => setModalOpen(true)}
					>
						{i18n.t("campaigns.buttons.add")}
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<Paper className={classes.mainPaper} variant="outlined">
				<Typography variant="body2" className={classes.hint}>
					{i18n.t("campaigns.hint")}
				</Typography>

				{loaded && campaigns.length === 0 ? (
					<div className={classes.empty}>
						<Typography variant="body1">{i18n.t("campaigns.empty")}</Typography>
					</div>
				) : (
					<ResponsiveTable>
						<TableHead>
							<TableRow>
								<TableCell>{i18n.t("campaigns.table.name")}</TableCell>
								<TableCell align="center">
									{i18n.t("campaigns.table.status")}
								</TableCell>
								<TableCell align="center">
									{i18n.t("campaigns.table.progress")}
								</TableCell>
								<TableCell align="center">
									{i18n.t("campaigns.table.sent")}
								</TableCell>
								<TableCell align="center">
									{i18n.t("campaigns.table.remaining")}
								</TableCell>
								<TableCell align="center">
									{i18n.t("campaigns.table.actions")}
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{campaigns.map(campaign => {
								const { counts } = campaign;
								const open = counts.pending + counts.sending;
								return (
									<TableRow key={campaign.id}>
										<TableCell>{campaign.name}</TableCell>
										<TableCell
											align="center"
											data-label={i18n.t("campaigns.table.status")}
										>
											<Tooltip title={campaign.pauseReason || ""}>
												<Chip
													size="small"
													color={CAMPAIGN_STATUS_COLOR[campaign.status]}
													variant={
														campaign.status === "running" ? "default" : "outlined"
													}
													label={i18n.t(`campaigns.status.${campaign.status}`)}
												/>
											</Tooltip>
										</TableCell>
										<TableCell
											align="center"
											data-label={i18n.t("campaigns.table.progress")}
										>
											<div className={classes.progress}>
												<LinearProgress
													variant="determinate"
													value={percentDone(counts)}
												/>
												<Typography variant="caption">
													{percentDone(counts)}%
												</Typography>
											</div>
										</TableCell>
										<TableCell
											align="center"
											data-label={i18n.t("campaigns.table.sent")}
										>
											{counts.sent} / {counts.total}
										</TableCell>
										<TableCell
											align="center"
											data-label={i18n.t("campaigns.table.remaining")}
										>
											{open > 0
												? `${open} · ~${formatDuration(
														campaign.estimatedSecondsRemaining
												  )}`
												: "—"}
										</TableCell>
										<TableCell align="center">
											<Tooltip title={i18n.t("campaigns.buttons.details")}>
												<IconButton
													size="small"
													onClick={() => setDetailsId(campaign.id)}
												>
													<VisibilityOutlined />
												</IconButton>
											</Tooltip>
											{campaign.status === "draft" && (
												<Tooltip title={i18n.t("campaigns.buttons.start")}>
													<IconButton
														size="small"
														onClick={() => act(campaign, "start")}
													>
														<PlayCircleOutline />
													</IconButton>
												</Tooltip>
											)}
											{campaign.status === "running" && (
												<Tooltip title={i18n.t("campaigns.buttons.pause")}>
													<IconButton
														size="small"
														onClick={() => act(campaign, "pause")}
													>
														<PauseCircleOutline />
													</IconButton>
												</Tooltip>
											)}
											{campaign.status === "paused" && (
												<Tooltip title={i18n.t("campaigns.buttons.resume")}>
													<IconButton
														size="small"
														onClick={() => act(campaign, "resume")}
													>
														<PlayCircleOutline />
													</IconButton>
												</Tooltip>
											)}
											{["draft", "running", "paused"].includes(
												campaign.status
											) && (
												<Tooltip title={i18n.t("campaigns.buttons.cancel")}>
													<IconButton
														size="small"
														onClick={() => setConfirm({ action: "cancel", campaign })}
													>
														<StopOutlined />
													</IconButton>
												</Tooltip>
											)}
											{canDelete(campaign.status) && (
												<Tooltip title={i18n.t("campaigns.buttons.delete")}>
													<IconButton
														size="small"
														onClick={() => setConfirm({ action: "delete", campaign })}
													>
														<DeleteOutline />
													</IconButton>
												</Tooltip>
											)}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</ResponsiveTable>
				)}
			</Paper>
		</MainContainer>
	);
};

export default Campaigns;
